import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  open: boolean;
  onClose: () => void;
}

// ─── cycle diagram ────────────────────────────────────────────────────────────

const CycleNode = ({
  title,
  sub,
  badge,
  color,
  bg,
}: {
  title: string;
  sub?: string;
  badge?: string;
  color: string;
  bg: string;
}) => (
  <Paper
    variant="outlined"
    sx={{ p: 1.5, borderTop: `3px solid ${color}`, backgroundColor: bg, textAlign: 'center' }}
  >
    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>{title}</Typography>
    {sub && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>{sub}</Typography>}
    {badge && (
      <Chip
        label={badge}
        size="small"
        sx={{ mt: 0.75, backgroundColor: color, color: '#fff', fontSize: '0.65rem', height: 20 }}
      />
    )}
  </Paper>
);

const LoopBack = ({ label }: { label: string }) => (
  <Box sx={{ textAlign: 'center', mt: 0.75 }}>
    <Typography variant="caption" fontWeight={700} sx={{ color: 'success.main' }}>
      ↺ {label}
    </Typography>
  </Box>
);

const WheelCycleDiagram: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <Box>
    {/* ── Start node: Sell PUT ── */}
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
      <Box sx={{ width: { xs: '90%', sm: 220 } }}>
        <CycleNode
          title="◆  Sell a PUT"
          sub="Target 15–25Δ · 7–14 days to expiry"
          badge="Collect premium upfront"
          color="#1976d2"
          bg="rgba(25,118,210,0.07)"
        />
      </Box>
    </Box>

    {/* ── Branch arrows (desktop only) ── */}
    <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', gap: 16, color: 'text.disabled', fontSize: '1.3rem', mb: 0.5 }}>
      <Typography sx={{ transform: 'scaleX(-1)', fontSize: 'inherit', lineHeight: 1 }}>↘</Typography>
      <Typography sx={{ fontSize: 'inherit', lineHeight: 1 }}>↘</Typography>
    </Box>

    {/* Mobile down arrow */}
    <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', py: 0.5, color: 'text.disabled' }}>↓</Box>

    {/* ── Two outcome columns (row on sm+, column on xs) ── */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start' }}>

      {/* LEFT: PUT Expires (most common) */}
      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
        <CycleNode
          title="PUT Expires Worthless"
          sub="Stock stays above your strike at expiry"
          badge="Most common · ~75–85%"
          color="#388e3c"
          bg="rgba(56,142,60,0.07)"
        />
        <Box sx={{ textAlign: 'center', py: 0.75, color: 'text.disabled' }}>↓</Box>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: 'rgba(56,142,60,0.04)', borderStyle: 'dashed' }}>
          <Typography variant="caption" color="success.main" fontWeight={700}>Keep full premium</Typography>
          <Typography variant="caption" color="text.secondary" display="block">No shares, no obligation</Typography>
        </Paper>
        <LoopBack label="Sell another PUT" />
      </Box>

      {/* RIGHT: PUT Assigned → stock → sell CALL */}
      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
        <CycleNode
          title="PUT Assigned"
          sub="Stock closes below strike at expiry"
          badge="~15–25% of trades"
          color="#f57c00"
          bg="rgba(245,124,0,0.07)"
        />
        <Box sx={{ textAlign: 'center', py: 0.75, color: 'text.disabled' }}>↓</Box>
        <CycleNode
          title="Hold 100 Shares"
          sub="Bought at your strike — premium reduces your cost basis"
          color="#7b1fa2"
          bg="rgba(123,31,162,0.07)"
        />
        <Box sx={{ textAlign: 'center', py: 0.75, color: 'text.disabled' }}>↓</Box>
        <CycleNode
          title="◆  Sell a CALL"
          sub="At or above your cost basis"
          badge="Collect more premium"
          color="#0288d1"
          bg="rgba(2,136,209,0.07)"
        />

        {/* Sub-branch arrows (desktop only) */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', gap: 8, color: 'text.disabled', fontSize: '1.1rem', mt: 0.5, mb: 0.5 }}>
          <Typography sx={{ transform: 'scaleX(-1)', fontSize: 'inherit', lineHeight: 1 }}>↘</Typography>
          <Typography sx={{ fontSize: 'inherit', lineHeight: 1 }}>↘</Typography>
        </Box>

        {/* Mobile down arrow */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', py: 0.5, color: 'text.disabled' }}>↓</Box>

        {/* CALL outcomes */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <CycleNode
              title="CALL Expires"
              sub="Stock stays below CALL strike"
              color="#43a047"
              bg="rgba(67,160,71,0.07)"
            />
            <LoopBack label="Sell next CALL" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CycleNode
              title="CALL Assigned"
              sub="Shares sold at CALL strike"
              badge="Full cycle! 🎉"
              color="#00897b"
              bg="rgba(0,137,123,0.07)"
            />
            <LoopBack label="Back to Sell PUT" />
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Box sx={{ textAlign: 'center', px: 2, minWidth: { xs: '45%', sm: 'auto' } }}>
    <Typography variant="h5" fontWeight={700} color="primary.main">{value}</Typography>
    <Typography variant="caption" fontWeight={600} display="block">{label}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Box>
);

// ─── IV bar chart ─────────────────────────────────────────────────────────────

const IvBar = ({ height, label, sublabel, color }: { height: number; label: string; sublabel: string; color: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: 80 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', height: 32, display: 'flex', alignItems: 'flex-end' }}>
      {sublabel}
    </Typography>
    <Box sx={{ width: 48, height, backgroundColor: color, borderRadius: '4px 4px 0 0', transition: 'height 0.3s' }} />
    <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center' }}>{label}</Typography>
  </Box>
);

// ─── main component ───────────────────────────────────────────────────────────

const WheelGuide: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = fullScreen;

  return (
  <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>The Wheel Strategy</Typography>
        <Typography variant="body2" color="text.secondary">
          Collect premium repeatedly on stocks you'd be happy to own
        </Typography>
      </Box>
      <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
    </DialogTitle>

    <DialogContent dividers>

      {/* ── big idea ─────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3, backgroundColor: 'primary.main', color: '#fff', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          The one-line idea
        </Typography>
        <Typography variant="body1">
          Options buyers pay you to take on the risk of price movements. The Wheel lets you collect that
          payment over and over — while only ever owning stocks you already wanted.
        </Typography>
      </Paper>

      {/* ── cycle diagram ─────────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        How the cycle works
      </Typography>

      <Box sx={{ mb: 3 }}>
        <WheelCycleDiagram isMobile={isMobile} />
      </Box>

      {/* ── worked example ────────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Worked example — MRVL at $90
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {[
          {
            step: '1',
            color: '#1976d2',
            action: 'Sell a $87 PUT expiring in 1 week',
            outcome: 'Collect $0.87/share = $87 cash. If MRVL stays above $87, pocket it.',
          },
          {
            step: '2',
            color: '#7b1fa2',
            action: 'MRVL drops to $84 — you\'re assigned',
            outcome: 'You buy 100 shares at $87. Real cost = $87 − $0.87 premium = $86.13.',
          },
          {
            step: '3',
            color: '#388e3c',
            action: 'Sell a $90 CALL expiring next week',
            outcome: 'Collect $1.20/share = $120. If MRVL rises above $90, shares sold at $90.',
          },
          {
            step: '✓',
            color: '#f57c00',
            action: 'Full cycle result',
            outcome: 'Premiums: $87 + $120 = $207. Stock: bought $8,700, sold $9,000 = +$300. Total: +$507 on $8,700 collateral.',
          },
        ].map(({ step, color, action, outcome }) => (
          <Box key={step} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 28, height: 28, borderRadius: '50%', backgroundColor: color,
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, mt: 0.3,
              }}
            >
              {step}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>{action}</Typography>
              <Typography variant="body2" color="text.secondary">{outcome}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── where's the edge ─────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Where does the edge come from?
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {[
          {
            icon: '📈',
            title: 'IV Premium',
            body: 'Options are usually priced slightly above their statistical fair value. Sellers collect this overpricing on every trade.',
          },
          {
            icon: '⏱️',
            title: 'Time Decay',
            body: 'Options lose value every day that passes. As the seller, time works in your favour — the option expires worthless if the stock holds still.',
          },
          {
            icon: '🎯',
            title: 'Controlled Strike',
            body: 'By targeting 15–25 delta strikes, you only get assigned ~15–25% of the time. You set the odds, not the market.',
          },
        ].map(({ icon, title, body }) => (
          <Paper key={title} variant="outlined" sx={{ p: 2, flex: 1, minWidth: { xs: '100%', sm: 160 } }}>
            <Typography fontSize="1.5rem" sx={{ mb: 0.5 }}>{icon}</Typography>
            <Typography variant="body2" fontWeight={700} gutterBottom>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{body}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ── iv visualisation ──────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        IV elevation — why timing matters
      </Typography>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Implied Volatility (IV) measures how expensive options are. Selling when IV is high earns more
          premium. Pryvo highlights tickers where current IV is elevated vs. their 12-week average.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, justifyContent: 'center', mb: 1.5 }}>
          <IvBar height={60} label="Low IV" sublabel="Cheap options, thin edge" color="#90caf9" />
          <IvBar height={100} label="Normal IV" sublabel="Typical week · 15–25Δ" color="#42a5f5" />
          <IvBar height={150} label="Elevated IV" sublabel="More premium · go further OTM" color="#1565c0" />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          The <strong>avg_iv_12w</strong> column in Pryvo compares today's IV to the last 3 months.
          Higher = more premium available.
        </Typography>
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            When IV is elevated: use 5–10Δ PUTs instead
          </Typography>
          <Typography variant="body2" color="text.secondary">
            High IV signals the market is pricing in a specific risk — earnings, a macro event, or unusual
            uncertainty. That means a larger-than-normal move is possible. Drop your delta to 5–10Δ (further
            out-of-the-money) to reduce your assignment probability. You still collect elevated premium, but
            give the stock more room to move against you.
          </Typography>
        </Box>
      </Paper>

      {/* ── key numbers ───────────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Numbers to know
      </Typography>
      <Box sx={{ display: 'flex', gap: 0, flexWrap: 'wrap', justifyContent: 'space-around', mb: 3 }}>
        <Stat label="Target Delta" value="0.15–0.25" sub="~15–25% chance of assignment" />
        <Stat label="Typical APR" value="30–60%" sub="on well-selected stocks" />
        <Stat label="DTE target" value="7–14 days" sub="1–2 weeks to expiry" />
        <Stat label="Wheel APR" value="premium only" sub="Net APR adds stock gain/loss" />
      </Box>

      {/* ── risk note ─────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 2.5, mb: 2, borderColor: 'warning.main', borderWidth: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>⚠️ The main risk — sharp drops</Typography>
        <Typography variant="body1">
          If the stock drops sharply after assignment, you hold shares at a loss. The premium collected
          partially offsets this, but a large drop can still hurt. Only wheel stocks you'd genuinely
          be comfortable holding long-term if the trade goes against you.
        </Typography>
      </Paper>

      {/* ── opportunity cost ──────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderColor: 'info.main', borderWidth: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>💡 The hidden cost — capped upside</Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          Selling PUTs limits how much you can make. If you're bullish on NVDA at $180 and sell a PUT
          instead of buying shares, you collect the premium — but your profit is capped there. If NVDA
          runs to $200, you don't participate in that $20 move.
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          The Wheel works best when you expect the stock to stay relatively range-bound or move only
          modestly. If you have strong conviction that a stock is about to make a big directional move,
          owning the shares outright will outperform the premium income.
        </Typography>
        <Box sx={{ p: 1.5, backgroundColor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            The flip side — uncertain markets favour the Wheel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When the market feels shaky and you're not sure which way things go, holding cash and wheeling
            it beats holding stocks. You collect premium while sitting in cash, and only end up owning shares
            at a price you chose in advance. Stocks can gap down 20% overnight — a cash-secured PUT at a
            15–25Δ strike gives you a built-in discount and a premium cushion before you're even exposed.
          </Typography>
        </Box>
      </Paper>

    </DialogContent>
  </Dialog>
  );
};

export default WheelGuide;
